import { useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { api } from '@api/v1/client'
import objectService from '@api/v1/objectService'
import useAppStore from '@store/appStore'
import DataGrid from '@components/common/DataGrid'
import LoadingSpinner from '@components/common/LoadingSpinner'
import CreateObjectModal from '@components/objects/CreateObjectModal'
import { inferFieldType, fmtValue } from '@utils/fieldTypes'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

/**
 * CC-53132 — the surface for a read that did NOT resolve.
 *
 * Deliberately neither the empty state nor a spinner. A paused read never
 * resolves on its own, so a spinner would sit there for as long as the browser
 * stayed offline; the empty state would assert, as fact, that the firm has none
 * of whatever the user came here to look at. Both are lies, in different tones.
 *
 * `role="alert"` because the user is staring at a screen that appears to answer
 * their question, and the one thing they need told is that it does not. The
 * retry is `refetch` — react-query resumes a paused query the moment the
 * connection returns, so the way out costs nothing.
 *
 * Markup mirrors the catalog page's blocked panel (`Reports.jsx`) on purpose:
 * this is the same fact about the same class of read, and it should not look
 * like a different kind of problem depending on which page you are standing on.
 */
const UnresolvedPanel = ({ headline, detail, onRetry, children }) => (
  <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
    <p className="font-medium">{headline}</p>
    <p className="mt-1 text-amber-800">{detail}</p>
    <div className="mt-3 flex items-center gap-3">
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
      >
        Try again
      </button>
      {children}
    </div>
  </div>
)

// ─── Smart Edit Field ────────────────────────────────────────────────────────

const EditField = ({ field, value, onChange }) => {
  const ft = inferFieldType(field.value, field.label, field.type)
  const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cb-accent/20 focus:border-cb-accent'

  switch (ft) {
    case 'boolean':
      return (
        <div className="flex items-center gap-3 py-2">
          <button
            type="button"
            onClick={() => onChange(!value && value !== true)}
            className={`relative w-10 h-5 rounded-full transition-colors ${
              value === true || value === 'True' ? 'bg-cb-accent' : 'bg-gray-300'
            }`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
              value === true || value === 'True' ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
          <span className="text-sm text-gray-600">
            {value === true || value === 'True' ? 'Yes' : 'No'}
          </span>
        </div>
      )

    case 'date':
      return (
        <input
          type="date"
          value={value ? dayjs(value).format('YYYY-MM-DD') : ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )

    case 'datetime':
      return (
        <input
          type="datetime-local"
          value={value ? dayjs(value).format('YYYY-MM-DDTHH:mm') : ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )

    case 'currency':
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
          <input
            type="number"
            step="0.01"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} pl-7`}
          />
        </div>
      )

    case 'number':
      return (
        <input
          type="number"
          step="any"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )

    case 'longtext':
      return (
        <textarea
          rows={4}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} resize-y`}
        />
      )

    default:
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )
  }
}

const DetailRow = ({ label, value, dataType }) => (
  <div className="flex items-start py-2.5 border-b border-gray-50 last:border-0">
    <dt className="w-44 shrink-0 text-sm text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900">{fmtValue(value, label, dataType)}</dd>
  </div>
)

// ─── List View ───────────────────────────────────────────────────────────────

const ObjectListView = ({ typeId, typeName, onSelect }) => {
  const listQuery = useQuery({
    queryKey: ['objectData', typeId],
    queryFn: () => api.get(`/objects/${typeId}`, { limit: 500 }),
    staleTime: 5 * 60 * 1000,
    enabled: !!typeId,
  })
  const { data: resp, error, refetch } = listQuery

  /*
   * CC-53132 — a read has three outcomes, and this page only ever handled two.
   *
   * `isLoading` is `isPending && isFetching`, so it is FALSE for a query that is
   * pending but NOT in flight — above all one react-query has PAUSED (its
   * default `networkMode: 'online'`, while `onlineManager` believes the browser
   * is offline). `queryFn` is never called: no request leaves the browser, so
   * nothing throws, nothing is logged, and there is nothing to find in a log on
   * either side. `resp` simply stays `undefined` forever.
   *
   * With `resp?.data || []` below, that produced a page reading "0 records" over
   * a grid reading "No matters found" — a firm-wide statement about the client's
   * data, made about a question nobody asked. This is a browser over every
   * object type in the tenant, so the same screen can say it about matters,
   * invoices or trust ledgers.
   *
   * `useReadState` rather than the bare `readState` because it carries the
   * permanent blocked-read diagnostic, which is the ONLY trace a paused read
   * leaves anywhere.
   */
  const listState = useReadState(`object-data:${typeId}`, listQuery)
  const listReady = listState === 'ready'

  // Left UNDEFINED on purpose while the read is unresolved. `undefined` and `[]`
  // are different answers and `DataGrid` now tells them apart (`Array.isArray`)
  // to decide whether it may describe itself as empty; collapsing them here with
  // `|| []` is the defect, one layer up. Both are plain property reads off the
  // cached response object, so they are also stable across renders — the `|| []`
  // they replace minted a fresh array every render straight into the columns
  // memo below, which is the ticking-value trap this repo has been bitten by.
  const rows = resp?.data
  const discoveredFields = resp?.meta?.fields

  /*
   * The count is the other half of the defect: `?? items.length` bottomed out at
   * that `|| []`, so an unresolved read printed a "0" it had not counted. It is
   * now `undefined` unless the read finished AND carried something countable — a
   * resolved response with no rows array (the CC-50321 shape problem) is not a
   * zero either.
   */
  const total = listReady ? (resp?.meta?.total ?? rows?.length) : undefined

  const columns = useMemo(() => {
    const cols = [{ id: 'name', header: 'Name', accessorKey: 'name', size: 200 }]
    const sample = rows || []
    const source = discoveredFields?.length > 0
      ? discoveredFields.slice(0, 12).map((f) => ({ id: f.id, header: f.label, accessorKey: f.id }))
      : sample.length > 0
        ? Object.keys(sample[0]).filter((k) => k !== 'id' && k !== 'name').slice(0, 12)
            .map((k) => ({ id: k, header: k, accessorKey: k }))
        : []
    for (const col of source) {
      cols.push({
        ...col,
        size: 150,
        cell: ({ getValue }) => {
          const v = getValue()
          return <span className="text-sm">{fmtValue(v, col.header)}</span>
        },
      })
    }
    return cols
  }, [discoveredFields, rows])

  const plural = typeName.toLowerCase()

  /* The state that used to render as "no records". react-query paused the fetch,
     so `isLoading` is false and the grid fell straight through to its empty
     message. A spinner would be almost as dishonest — a paused query never
     resolves by itself — so say what actually happened and offer the way out. */
  if (listState === 'blocked') {
    return (
      <UnresolvedPanel
        headline={`Can't reach the server, so we haven't loaded your ${plural}.`}
        detail={`This is not an empty list — nothing has been deleted. Your connection looks offline; check it and try again.`}
        onRetry={() => refetch()}
      />
    )
  }

  /* A failed read and an empty one are different facts, and a user shown the
     wrong one has no reason to retry: they conclude the firm has no records of
     this type and act on it. Same panel, different first sentence, because the
     recovery is the same but the cause is not. */
  if (listState === 'failed') {
    return (
      <UnresolvedPanel
        headline={`Couldn't load ${plural}.`}
        detail={`${error?.message || 'The server did not answer.'} This is not an empty list — nothing has been deleted.`}
        onRetry={() => refetch()}
      />
    )
  }

  /*
   * 'idle' — the per-page decision, stated because every one of the five states
   * has to render SOMETHING and this is the one that is easy to drop.
   *
   * This query IS conditionally enabled (`enabled: !!typeId`), so idle means the
   * ordinary "not asked yet" and must NOT be dressed up as a failure. It is also
   * unreachable today: the parent mounts this component only inside its
   * `activeTypeId ?` branch, so `typeId` is always truthy here. It still gets its
   * own branch rather than riding with `loading`, for two reasons — folding a
   * deliberately-not-asked read into the spinner is how you get a spinner that
   * never stops, and leaving a fifth state uncovered is how the first pass at
   * this fix shipped a blank page. Copy is neutral: nothing failed, there is
   * simply no type to ask about yet.
   */
  if (listState === 'idle') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
        <p className="text-sm text-gray-500">Choose an object type to load its records.</p>
      </div>
    )
  }

  // Only 'loading' and 'ready' reach here. The grid owns the spinner for the
  // former, and only the latter is allowed to describe the firm's data.
  return (
    <>
      {total != null && (
        <p className="text-sm text-gray-500 mb-4">
          {total} {total === 1 ? 'record' : 'records'}
        </p>
      )}
      <DataGrid
        columns={columns}
        data={rows}
        isLoading={listState === 'loading'}
        onRowClick={(row) => onSelect(row.id)}
        emptyMessage={`No ${plural} found`}
        /* Reachable on this path only for a "ready" response that carried no rows
           array at all — i.e. a shape we could not read, which is not a zero. The
           grid renders this with `role="alert"` instead of `emptyMessage`. */
        unresolvedMessage={`We couldn't read the ${plural} list, so it is not known to be empty. Reload to try again.`}
        loadingMessage={`Loading ${plural}...`}
        exportFilename={typeName.toLowerCase().replace(/\s+/g, '-')}
        pageSize={50}
      />
    </>
  )
}

// ─── Detail View ─────────────────────────────────────────────────────────────

const ObjectDetailView = ({ typeId, itemId, typeName, onBack }) => {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editValues, setEditValues] = useState({})

  // Reuse the list query cache — no single-item endpoint exists
  const detailQuery = useQuery({
    queryKey: ['objectData', typeId],
    queryFn: () => api.get(`/objects/${typeId}`, { limit: 500 }),
    staleTime: 5 * 60 * 1000,
    enabled: !!typeId,
  })
  const { data: listResp, error, refetch } = detailQuery

  /*
   * CC-53132 again, and this is the nastier half of it.
   *
   * Because there is no single-item endpoint, "does this record exist?" is
   * answered by searching the LIST response. So when the list read never happens
   * — react-query pauses the fetch, `isLoading` is false because nothing is in
   * flight, `listResp` stays undefined, `items` fell back to `[]` — `find()`
   * returns undefined and the page rendered **"Record not found"**. That is not
   * a soft failure: it is the app telling a user that a matter, an invoice or a
   * payment they are looking straight at does not exist, with no spinner and no
   * error, about a request that was never sent.
   *
   * "Not found" is now reachable only from a read that actually completed.
   */
  const detailState = useReadState(`object-detail:${typeId}`, detailQuery)

  // Undefined, not `[]` / not `{}`: see the list view. `item` therefore stays
  // undefined for an unresolved read too, which is why the branches below have
  // to run BEFORE the not-found check rather than after it.
  const items = listResp?.data
  const fields = listResp?.meta?.fields
  const item = useMemo(() => items?.find((i) => i.id === itemId), [items, itemId])

  // Parse fields into structured display
  const parsed = useMemo(() => {
    if (!item) return { allFields: [] }
    const fieldMap = {}
    for (const f of fields || []) fieldMap[f.id] = f

    const allFields = []
    for (const [fid, val] of Object.entries(item)) {
      if (fid === 'id') continue
      if (fid === 'name') {
        allFields.unshift({ id: 'name', label: 'Name', value: val, type: 'text' })
        continue
      }
      const field = fieldMap[fid]
      const label = field?.label || fid
      if (val != null && val !== '') {
        allFields.push({ id: fid, label, value: val, type: field?.dataType || 'text' })
      }
    }
    return { name: item.name, id: item.id, allFields }
  }, [item, fields])

  const saveMutation = useMutation({
    mutationFn: (body) => objectService.updateObject(typeId, itemId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objectData', typeId] })
      setEditing(false)
      setEditValues({})
    },
  })

  const startEditing = useCallback(() => {
    const vals = {}
    for (const f of parsed.allFields) {
      vals[f.id] = f.value
    }
    setEditValues(vals)
    setEditing(true)
  }, [parsed.allFields])

  const handleSave = useCallback(() => {
    saveMutation.mutate(editValues)
  }, [editValues, saveMutation])

  // Offered alongside the retry on every unresolved branch: the read may stay
  // unresolved indefinitely, and the user must not be stranded on this record.
  const backLink = (
    <button
      type="button"
      onClick={onBack}
      className="text-xs font-medium text-amber-900 underline hover:no-underline"
    >
      Back to {typeName}
    </button>
  )

  /* The paused read. This is where "Record not found" came from — see the note
     on `detailState` above. Answer the user's actual question first: the record
     is almost certainly fine, we just never asked. */
  if (detailState === 'blocked') {
    return (
      <UnresolvedPanel
        headline="Can't reach the server, so we haven't loaded this record."
        detail="This does not mean the record is missing — nothing was deleted, and no request was sent. Your connection looks offline; check it and try again."
        onRetry={() => refetch()}
      >
        {backLink}
      </UnresolvedPanel>
    )
  }

  /* A failed read said "Record not found" too, which sends the user off to
     re-create something that already exists. */
  if (detailState === 'failed') {
    return (
      <UnresolvedPanel
        headline="Couldn't load this record."
        detail={`${error?.message || 'The server did not answer.'} This does not mean the record is missing.`}
        onRetry={() => refetch()}
      >
        {backLink}
      </UnresolvedPanel>
    )
  }

  /*
   * 'idle' — same per-page decision as the list view, and stated again here
   * because this component is reached down a different branch of the parent.
   * The query is conditionally enabled (`enabled: !!typeId`), so idle is the
   * ordinary "not asked yet", never an error; it is unreachable today because
   * the parent only mounts this component with a truthy `activeTypeId`. Given
   * its own branch anyway: folding it into the spinner below would strand the
   * page on a spinner that can never end, and leaving the fifth state uncovered
   * would fall through to the not-found message — the exact bug being fixed.
   */
  if (detailState === 'idle') {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">Choose an object type to load this record.</p>
        <button onClick={onBack} className="text-sm text-cb-accent-text hover:underline mt-2">
          Back to list
        </button>
      </div>
    )
  }

  if (detailState === 'loading') {
    return (
      <div className="p-12 flex justify-center">
        <LoadingSpinner message="Loading record..." />
      </div>
    )
  }

  // Only 'ready' reaches here, so an absent item is now genuinely an absent
  // record rather than an unanswered question.
  if (!item) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">Record not found</p>
        <button onClick={onBack} className="text-sm text-cb-accent-text hover:underline mt-2">
          Back to list
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Back + Header */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        Back to {typeName}
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display text-gray-900">{parsed.name || `Record`}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {typeName} &middot; {parsed.allFields.length} fields
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button
                onClick={() => { setEditing(false); setEditValues({}) }}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="px-3 py-1.5 text-xs font-medium text-white bg-cb-accent rounded-lg hover:bg-cb-accent/90 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button
              onClick={startEditing}
              className="px-3 py-1.5 text-xs font-medium text-white bg-cb-accent rounded-lg hover:bg-cb-accent/90"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {saveMutation.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
          Save failed: {saveMutation.error?.message || 'Unknown error'}
        </div>
      )}

      {saveMutation.isSuccess && !editing && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
          Record saved successfully
        </div>
      )}

      {/* Fields */}
      {editing ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Edit Fields</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {parsed.allFields.map((f) => {
              const ft = inferFieldType(f.value, f.label, f.type)
              return (
                <div key={f.id} className={ft === 'longtext' ? 'sm:col-span-2' : ''}>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-1">
                    {f.label}
                    <span className="text-[10px] text-gray-300 font-normal">{ft}</span>
                  </label>
                  <EditField
                    field={f}
                    value={editValues[f.id]}
                    onChange={(v) => setEditValues((p) => ({ ...p, [f.id]: v }))}
                  />
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Split fields into two columns */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Details</h3>
            <dl>
              {parsed.allFields.slice(0, Math.ceil(parsed.allFields.length / 2)).map((f) => (
                <DetailRow key={f.id} label={f.label} value={f.value} dataType={f.type} />
              ))}
            </dl>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Additional</h3>
            <dl>
              {parsed.allFields.slice(Math.ceil(parsed.allFields.length / 2)).map((f) => (
                <DetailRow key={f.id} label={f.label} value={f.value} dataType={f.type} />
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Objects Page ───────────────────────────────────────────────────────

const Objects = () => {
  useDocumentTitle('Objects')
  const { objectType: urlType, id: urlItemId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const objectTypes = useAppStore((s) => s.objectTypes)
  const [showCreate, setShowCreate] = useState(false)

  // All available types sorted by name
  const sortedTypes = useMemo(() => {
    if (!objectTypes) return []
    return [...objectTypes].sort((a, b) =>
      (a.pluralName || '').localeCompare(b.pluralName || '')
    )
  }, [objectTypes])

  // Current type ID — from URL or default to first available
  const activeTypeId = urlType || (sortedTypes.length > 0 ? String(sortedTypes[0].objectType) : '')

  const typeMeta = useMemo(
    () => sortedTypes.find((t) => String(t.objectType) === String(activeTypeId)),
    [sortedTypes, activeTypeId]
  )
  const typeName = typeMeta?.pluralName || typeMeta?.singularName || `Type ${activeTypeId}`
  const singularName = typeMeta?.singularName || typeName

  const handleTypeChange = useCallback((newTypeId) => {
    navigate(`/objects/${newTypeId}`, { replace: true })
  }, [navigate])

  const handleSelectItem = useCallback((itemId) => {
    navigate(`/objects/${activeTypeId}/${itemId}`)
  }, [navigate, activeTypeId])

  const handleBackToList = useCallback(() => {
    navigate(`/objects/${activeTypeId}`)
  }, [navigate, activeTypeId])

  /*
   * NOT a CC-53132 site, and left alone deliberately — don't "fix" it.
   *
   * `objectTypes` is not a read on this page: it is an appStore value whose
   * contract already draws the distinction this ticket is about (`null` = not
   * loaded yet, `[]` = loaded and genuinely empty — appStore.js:17), and
   * `setObjectTypes` is only ever called from a SUCCESSFUL fetch
   * (`useObjectTypes.js`). So "0 types available" and "Select an object type
   * above to get started" below are already gated on a completed read and are
   * never printed about an unanswered one.
   *
   * What is NOT covered here: a FAILED `useObjectTypes` leaves the store at
   * `null`, so this renders its spinner indefinitely. That is a separate defect
   * in a separate file (the query has no error path back to this page at all)
   * and it does not fabricate a count, so it is not fixed here.
   */
  if (!objectTypes) {
    return (
      <div className="max-w-7xl mx-auto p-12 flex justify-center">
        <LoadingSpinner message="Loading object types..." />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header with type switcher */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-display text-gray-900">
              {urlItemId ? singularName : 'Objects'}
            </h1>
            {!urlItemId && (
              <p className="text-sm text-gray-500 mt-1">
                Browse and manage all Centerbase object types
              </p>
            )}
          </div>
        </div>
        {!urlItemId && activeTypeId && (
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2.5 bg-cb-accent text-white rounded-lg text-sm
              font-medium hover:bg-cb-accent/90 transition-colors shadow-sm"
          >
            + New {singularName}
          </button>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <CreateObjectModal
          typeId={activeTypeId}
          typeName={singularName}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            queryClient.invalidateQueries({ queryKey: ['objectData', activeTypeId] })
          }}
        />
      )}

      {/* Object Type Switcher */}
      {!urlItemId && (
        <div className="mb-6">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
            Object Type
          </label>
          <div className="flex items-center gap-3">
            <select
              value={activeTypeId}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:ring-2 focus:ring-cb-accent/20 focus:border-cb-accent min-w-[260px]"
            >
              {sortedTypes.map((t) => (
                <option key={t.objectType} value={t.objectType}>
                  {t.pluralName || t.singularName} ({t.objectType})
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-500">
              {sortedTypes.length} types available
            </span>
          </div>

          {/* Quick-access type pills */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {sortedTypes.slice(0, 20).map((t) => (
              <button
                key={t.objectType}
                onClick={() => handleTypeChange(String(t.objectType))}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  String(t.objectType) === String(activeTypeId)
                    ? 'bg-cb-accent text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t.pluralName || t.singularName}
              </button>
            ))}
            {sortedTypes.length > 20 && (
              <span className="px-2.5 py-1 text-xs text-gray-500">
                +{sortedTypes.length - 20} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Content: either detail or list */}
      {urlItemId ? (
        <ObjectDetailView
          typeId={activeTypeId}
          itemId={urlItemId}
          typeName={typeName}
          onBack={handleBackToList}
        />
      ) : activeTypeId ? (
        <ObjectListView
          typeId={activeTypeId}
          typeName={typeName}
          onSelect={handleSelectItem}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-sm text-gray-500">Select an object type above to get started</p>
        </div>
      )}
    </div>
  )
}

export default Objects
